import React, { useState } from "react";
import { View, StyleSheet, TouchableOpacity } from "react-native";
import { CheckCircle, Warning, Star, Lightbulb, CaretDown, CaretUp } from "@phosphor-icons/react";
import CustomText from "../../common/general/CustomText/CustomText";

export interface AssignmentAnalysis {
  title: string;
  effectiveness?: string;
  feedback?: string;
  understandingGauge?: string;
  suggestions?: string[];
  assignmentId?: string;
  courseCode?: string;
}

interface EffectivenessBadgeProps {
  rating: string;
}

const EffectivenessBadge = ({ rating }: EffectivenessBadgeProps) => {
  const getBadgeStyle = () => {
    switch (rating?.toLowerCase()) {
      case "excellent":
        return { bg: "#ECFDF3", text: "#027A48", border: "#A6F4C5" };
      case "good":
        return { bg: "#EEF4FF", text: "#3538CD", border: "#C7D7FE" };
      case "fair":
        return { bg: "#FFFAEB", text: "#B54708", border: "#FEDF89" };
      case "poor":
        return { bg: "#FEF3F2", text: "#B42318", border: "#FECDCA" };
      default:
        return { bg: "#F2F4F7", text: "#344054", border: "#E4E7EC" };
    }
  };

  const style = getBadgeStyle();

  return (
    <View style={[styles.effectivenessBadge, { backgroundColor: style.bg }]}>
      <CustomText useTranslationText={false} text={rating} size="xs" weight="medium" style={{ color: style.text }} />
    </View>
  );
};

interface AssignmentAccordionProps {
  analysis: AssignmentAnalysis;
}

const AssignmentAccordion = ({ analysis }: AssignmentAccordionProps) => {
  const [expanded, setExpanded] = useState(false);

  const getIcon = () => {
    switch (analysis.effectiveness?.toLowerCase()) {
      case "excellent":
        return <CheckCircle size={20} color="#027A48" />;
      case "good":
        return <CheckCircle size={20} color="#1d4ed8" />;
      case "fair":
        return <Warning size={20} color="#B54708" />;
      case "poor":
        return <Warning size={20} color="#dc2626" />;
      default:
        return <CheckCircle size={20} color="#667085" />;
    }
  };

  const getIconContainerColor = () => {
    switch (analysis.effectiveness?.toLowerCase()) {
      case "excellent":
        return "#f0fdf4";
      case "good":
        return "#eff6ff";
      case "fair":
        return "#fefce8";
      case "poor":
        return "#f7f4f2";
      default:
        return "#F2F4F7";
    }
  };

  const getDisplayTitle = () => {
    if (analysis.courseCode) {
      return `${analysis.courseCode}: ${analysis.title}`;
    }
    return analysis.title;
  };

  return (
    <TouchableOpacity style={styles.assignmentAccordion} onPress={() => setExpanded(!expanded)} activeOpacity={0.7}>
      <View style={styles.assignmentAccordionHeader}>
        <View style={[styles.iconContainer, { backgroundColor: getIconContainerColor() }]}>{getIcon()}</View>
        <View style={styles.assignmentAccordionContent}>
          <View style={styles.assignmentTitleRow}>
            <CustomText
              useTranslationText={false}
              text={getDisplayTitle()}
              size="m"
              weight="medium"
              style={styles.assignmentTitle}
            />
            <EffectivenessBadge rating={analysis.effectiveness || ""} />
          </View>
          <CustomText
            useTranslationText={false}
            text={analysis.understandingGauge || ""}
            size="s"
            style={styles.assignmentDescription}
            numberOfLines={expanded ? undefined : 2}
          />
        </View>
        {expanded ? <CaretUp size={16} color="#98A2B3" /> : <CaretDown size={16} color="#98A2B3" />}
      </View>

      {expanded && analysis.feedback && (
        <View style={styles.assignmentSuggestions}>
          <CustomText
            useTranslationText={false}
            text="Feedback"
            size="xs"
            weight="bold"
            style={styles.suggestionsLabel}
          />
          <CustomText useTranslationText={false} text={analysis.feedback} size="xs" style={styles.suggestionText} />
        </View>
      )}
      {expanded && analysis.suggestions && analysis.suggestions.length > 0 && (
        <View style={analysis.feedback ? styles.assignmentSuggestionsNoBorder : styles.assignmentSuggestions}>
          <CustomText
            useTranslationText={false}
            text="Suggestions for improvement"
            size="xs"
            weight="bold"
            style={styles.suggestionsLabel}
          />
          {analysis.suggestions.map((s: string, idx: number) => (
            <View key={idx} style={styles.suggestionItem}>
              <Lightbulb size={14} color="#F79009" />
              <CustomText useTranslationText={false} text={s} size="xs" style={styles.suggestionText} />
            </View>
          ))}
        </View>
      )}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  effectivenessBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  assignmentAccordion: {
    backgroundColor: "#FFFFFF",
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#EAECF0",
    padding: 16,
    overflow: "hidden",
  },
  assignmentAccordionHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 16,
  },
  iconContainer: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  assignmentAccordionContent: {
    flex: 1,
    minWidth: 0,
  },
  assignmentTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 4,
  },
  assignmentTitle: {
    color: "#101828",
  },
  assignmentDescription: {
    color: "#475467",
    lineHeight: 20,
  },
  assignmentSuggestions: {
    marginTop: 16,
    marginLeft: -16,
    marginRight: -16,
    paddingTop: 16,
    paddingLeft: 16,
    paddingRight: 16,
    borderTopWidth: 1,
    borderTopColor: "#E4E7EC",
  },
  assignmentSuggestionsNoBorder: {
    marginTop: 4,
    marginLeft: -16,
    marginRight: -16,
    paddingTop: 16,
    paddingLeft: 16,
    paddingRight: 16,
  },
  suggestionsLabel: {
    color: "#667085",
    marginBottom: 8,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  suggestionItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    marginBottom: 6,
  },
  suggestionText: {
    color: "#475467",
    flex: 1,
    lineHeight: 18,
  },
});

export default AssignmentAccordion;
